import { RealtimeClient } from "@web-monorepo/realtime/toolkit-iframe";
import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { useToolkitSession } from "src/pods/auth/useToolkitSession";
import {
  DISPLAY_CLOSE,
  DISPLAY_OPEN,
  FETCH_HOST_STATE,
  FETCH_HOST_STATE_DONE,
  RECEIVE_DISPLAY_CLOSE,
  RECEIVE_DISPLAY_OPEN,
  RECEIVE_REMOTE_CLOSE,
  REMOTE_CLOSE,
  SHARE_OVER_WIRE_RECEIVE,
  SHARE_OVER_WIRE_SEND,
} from "./data/pairing";
import { getToolkitRealtimeClientId, isLocalToolkitRealtimeSource } from "./clientId";
import { isDisplay, isRemote, mountPoint, classId, role } from "./util/role";
import getIframeAdapter, { isStandaloneMode, iframeNatsDisabled } from "./adapter";
import { navigate } from "src/utils/reactRouterNavigate";
import {
  dispatchToolkitReduxAction,
  dispatchToolkitReduxActionSilently,
  subscribeToolkitReduxActions,
} from "src/state/toolkitReduxStore";
import { mapFromAction, mapFromActionToNats, mapToAction } from "./index";
import {
  useRealtime,
  realtimeInit,
  realtimePublish,
  realtimeClose,
  subscribeReconnect,
  getReconnectSnapshot,
} from "src/util/realtime";
import { getToolkitRealtimeState, toolkitRealtimeActions } from "./toolkitRealtimeStore";
import { createPeerDetection, isPeerAction } from "./nats/peerDetection";
import { mapNatsToRedux } from "./nats/subscribeBridge";
import env from "shared/util/env";

import type { RealtimePayload } from "@web-monorepo/realtime";
import type { ToolkitAction } from "src/state/toolkitReduxStore";

declare global {
  interface Window {
    rtcClient?: RealtimeClient;
  }
}

const NATS_TOOLKIT_ACTIONS = [
  "toolkit:send-groups",
  "toolkit:send-radio",
  "toolkit:send-noise",
  "toolkit:send-microphone-error",
  "toolkit:send-ideas",
  "toolkit:display-open",
  "toolkit:display-close",
  "toolkit:remote-close",
  "toolkit:share-over-wire",
  "toolkit:peer-enter",
  "toolkit:peer-depart",
  "toolkit:peer-ping",
  "toolkit:peer-pong",
] as const satisfies readonly RealtimePayload["action"][];

type ToolkitNatsPayload = Extract<RealtimePayload, { action: (typeof NATS_TOOLKIT_ACTIONS)[number] }>;

type ReceiveDisplayOpenAction = {
  type: string;
  payload?: {
    action?: ToolkitAction;
    path: string;
  };
};

type HandleRealtimeActionArgs = {
  action: ToolkitAction;
  scheduleNavigation: (path: string) => void;
};

function getRemoteRoute() {
  return `/apps/remote/${mountPoint}/${classId}`;
}

function handleHostStateAction(action: ToolkitAction) {
  if (action.type === FETCH_HOST_STATE) {
    return true;
  }

  if (action.type === FETCH_HOST_STATE_DONE) {
    toolkitRealtimeActions.setPaired(
      Boolean(
        action.payload && typeof action.payload === "object" && "hasHost" in action.payload && action.payload.hasHost,
      ),
    );
    return true;
  }

  return false;
}

function handleDisplayAction({ action, scheduleNavigation }: HandleRealtimeActionArgs) {
  if (action.type === DISPLAY_OPEN) {
    toolkitRealtimeActions.setDisplayOn(getToolkitRealtimeState().paired);
    if (isRemote && mountPoint === "teach") {
      scheduleNavigation(getRemoteRoute());
    }
    return true;
  }

  if (action.type === RECEIVE_DISPLAY_OPEN) {
    toolkitRealtimeActions.setDisplayOn(true);
    if (isDisplay) {
      const { payload } = action as ReceiveDisplayOpenAction;
      if (payload?.action) {
        dispatchToolkitReduxActionSilently(payload.action);
      }
      if (payload?.path) {
        navigate(payload.path, { replace: true });
      }
    }
    return true;
  }

  if (action.type === RECEIVE_DISPLAY_CLOSE || action.type === DISPLAY_CLOSE) {
    toolkitRealtimeActions.setDisplayOn(false);
    if (isDisplay) {
      scheduleNavigation("/apps/display");
    }
    return true;
  }

  return false;
}

function handleRemoteAction(action: ToolkitAction) {
  if (action.type === REMOTE_CLOSE || action.type === RECEIVE_REMOTE_CLOSE) {
    if (isRemote) {
      navigate(getRemoteRoute(), { replace: true });
    }
    return true;
  }

  if (action.type === SHARE_OVER_WIRE_SEND || action.type === SHARE_OVER_WIRE_RECEIVE) {
    if (action.payload) {
      dispatchToolkitReduxActionSilently(action.payload as ToolkitAction);
    }
    return true;
  }

  return false;
}

function handleRealtimeAction(args: HandleRealtimeActionArgs) {
  if (handleHostStateAction(args.action)) {
    return;
  }

  if (handleDisplayAction(args)) {
    return;
  }

  handleRemoteAction(args.action);
}

export function getNatsPublishRequest(action: ToolkitAction, userId: string, clientId: string) {
  if (!isLocalToolkitRealtimeSource(clientId)) {
    return null;
  }

  const message = mapFromActionToNats(action, clientId);
  if (!message) {
    return null;
  }

  return {
    topic: `user.${userId}`,
    message,
  };
}

function publishNatsAction(action: ToolkitAction, userId: string, clientId: string) {
  // In iframe-only mode toolkit has no NATS connection; the iframe bridge relays to teach.
  if (iframeNatsDisabled) {
    return;
  }
  const publishRequest = getNatsPublishRequest(action, userId, clientId);
  if (publishRequest) {
    realtimePublish(publishRequest.topic, publishRequest.message);
  }
}

function clonePayload(payload: Record<string, unknown> | undefined | null): Record<string, unknown> | undefined {
  if (payload == null || typeof payload !== "object") {
    return undefined;
  }
  return JSON.parse(JSON.stringify(payload)) as Record<string, unknown>;
}

function publishIframeAction(action: ToolkitAction, client: RealtimeClient | null) {
  const message = client ? mapFromAction(action) : null;
  if (client && message?.type) {
    void client.publish(message.type, clonePayload(message.payload));
  }
}

export function flushQueuedRealtimeActions(
  actions: ToolkitAction[],
  client: RealtimeClient | null,
  userId: string | null,
  clientId: string | null,
) {
  if (!userId || !clientId) {
    return actions;
  }

  for (const action of actions) {
    publishIframeAction(action, client);
    publishNatsAction(action, userId, clientId);
  }

  return [];
}

function dispatchMappedRealtimeMessage(type: string, payload: Record<string, unknown>) {
  const action = mapToAction({ type, payload });
  if (!action) {
    return;
  }

  const actions = Array.isArray(action) ? action : [action];
  for (const mappedAction of actions) {
    dispatchToolkitReduxAction(mappedAction as ToolkitAction);
  }
}

function getRealtimeSetup(
  isLoading: boolean,
  sessionType: string | undefined,
  channel: string | null | undefined,
  userId: string | null | undefined,
): { channel: string; userId: string; clientId: string; clientRole: NonNullable<typeof role> } | null {
  if (isLoading || sessionType !== "teacher" || !role) {
    return null;
  }

  const clientId = getToolkitRealtimeClientId();
  if (!channel || !userId || !clientId) {
    return null;
  }

  return { channel, userId, clientId, clientRole: role };
}

// Checks if NATS subscription should be enabled for standalone mode (mobile remote, standalone display).
// Requires mqttChannels.self to prevent AuthorizationError when permissions are not loaded.
function isStandaloneSubscriptionEnabled(
  isLoading: boolean,
  sessionType: string | undefined,
  mqttChannel: string | null | undefined,
): boolean {
  return isStandaloneMode && (isRemote || isDisplay) && !isLoading && sessionType === "teacher" && !!mqttChannel;
}

export function useRealtimeRuntime() {
  const { isLoading, session } = useToolkitSession();
  const natsRecoveryGeneration = useSyncExternalStore(subscribeReconnect, getReconnectSnapshot);
  const clientRef = useRef<RealtimeClient | null>(null);
  const peerDetectionRef = useRef<ReturnType<typeof createPeerDetection> | null>(null);
  const userIdRef = useRef<string | null>(null);
  const clientIdRef = useRef<string | null>(null);
  const queuedPublishActionsRef = useRef<ToolkitAction[]>([]);
  const timeoutIdsRef = useRef<number[]>([]);

  useEffect(() => {
    const scheduleNavigation = (path: string) => {
      const timeoutId = window.setTimeout(() => {
        navigate(path, { replace: true });
        timeoutIdsRef.current = timeoutIdsRef.current.filter((id) => id !== timeoutId);
      }, 30);

      timeoutIdsRef.current.push(timeoutId);
    };

    const unsubscribe = subscribeToolkitReduxActions((action) => {
      handleRealtimeAction({
        action,
        scheduleNavigation,
      });

      if (userIdRef.current && clientIdRef.current) {
        publishIframeAction(action, clientRef.current);
        publishNatsAction(action, userIdRef.current, clientIdRef.current);
      } else {
        queuedPublishActionsRef.current.push(action);
      }
    });

    return () => {
      unsubscribe();
      for (const timeoutId of timeoutIdsRef.current) {
        window.clearTimeout(timeoutId);
      }
      timeoutIdsRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (!isRemote && !isDisplay) {
      return;
    }

    const setup = getRealtimeSetup(
      isLoading,
      session?.type,
      session?.userConfig?.mqttChannels?.self,
      session?.teacher?._id,
    );
    if (!setup) {
      return;
    }

    const { channel, userId, clientId, clientRole } = setup;

    userIdRef.current = userId;
    clientIdRef.current = clientId;
    // Iframe-only mode: skip opening toolkit's own NATS connection. Messages flow
    // through the IframeAdapter/RealtimeClient postMessage bridge set up below.
    if (!iframeNatsDisabled) {
      void realtimeInit();
    }

    // Set up IframeAdapter + RealtimeClient for receiving postMessages from
    // the teach parent window. The teach side's useToolkitNatsIframeBridge
    // converts NATS messages to postMessage format and sends them here.
    const iframeAdapter = getIframeAdapter();
    if (iframeAdapter) {
      // `RealtimeClient` has no host role: teach plays host by hand in
      // `useToolkitIframeRealtimeHost`. Toolkit is only ever a remote or a display, so
      // narrow the URL-derived role to the two the client still accepts.
      //
      // `role`'s type still admits "host" because `nats/peerDetection.ts` keys its own
      // NATS-side host branches off the same `util/role.ts` `Role` — a separate role from
      // the `CLIENT_ROLE.HOST` that was deleted from `RealtimeClient`. Dropping the
      // fallthrough means dropping "host" from `Role`, which would re-route a
      // `/apps/host/...` URL to *remote* through role.ts's legacy query-param branch. So
      // the fallthrough stays: that URL, which no build produces, gets a display client
      // rather than a host one, because there is no host one to get.
      const client = new RealtimeClient(
        clientRole === "remote" ? "remote" : "display",
        channel,
        iframeAdapter,
        clientId,
      );
      clientRef.current = client;

      if (env.isTesting) {
        window.rtcClient = client;
      }

      // Iframe-only mode: toolkit has no NATS connection, so peer detection can't
      // publish to NATS directly. Run it over the iframe bridge instead — publish
      // straight through the adapter (not client.publish, which host-gates remotes
      // and would block the discovery ping). Teach is the peer host on the bridge
      // (it answers/announces to this toolkit) and independently on NATS (for
      // devices on other machines). In dual mode (switch off) presence rides
      // RealtimeClient's own protocol, unchanged.
      if (iframeNatsDisabled) {
        const peerDetection = createPeerDetection({
          role: clientRole,
          userId,
          clientId,
          // The bridge multiplexes on the message `topic`, not the realtime topic, so
          // ignore `_topic` and send over the single iframe `channel` (unlike the
          // standalone `publish: realtimePublish`, which routes by topic on NATS).
          publish: (_topic, { action, payload }) => {
            void iframeAdapter.publish(channel, { sender: clientId, topic: action, payload });
          },
          onPeerChange: ({ hasPeers }) => {
            toolkitRealtimeActions.setPaired(hasPeers);
          },
        });
        peerDetectionRef.current = peerDetection;
        peerDetection.start();
      }

      client.on("message", (...args: unknown[]) => {
        const [type, payload] = args as [string, Record<string, unknown>];
        if (peerDetectionRef.current && isPeerAction(type)) {
          peerDetectionRef.current.handleNatsEvent({ action: type, payload });
          return;
        }
        dispatchMappedRealtimeMessage(type, payload);
      });
    } else {
      // Standalone mode (mobile remote, standalone display): use NATS peer detection
      const peerDetection = createPeerDetection({
        role: clientRole,
        userId,
        clientId,
        publish: realtimePublish,
        onPeerChange: ({ hasPeers }) => {
          toolkitRealtimeActions.setPaired(hasPeers);
        },
      });
      peerDetectionRef.current = peerDetection;
      peerDetection.start();
    }

    queuedPublishActionsRef.current = flushQueuedRealtimeActions(
      queuedPublishActionsRef.current,
      clientRef.current,
      userId,
      clientId,
    );

    const client = clientRef.current;
    const peerDetection = peerDetectionRef.current;
    return () => {
      clientRef.current = null;
      peerDetectionRef.current = null;
      userIdRef.current = null;
      clientIdRef.current = null;

      if (env.isTesting && client && window.rtcClient === client) {
        delete window.rtcClient;
      }

      if (peerDetection) {
        peerDetection.destroy();
      }

      if (!iframeNatsDisabled) {
        void realtimeClose();
      }
      if (client) {
        client.close();
      }
    };
  }, [isLoading, session?.type, session?.teacher?._id, session?.userConfig?.mqttChannels?.self]);

  useEffect(() => {
    if (natsRecoveryGeneration === 0 || iframeNatsDisabled) return;
    void realtimeInit();
  }, [natsRecoveryGeneration]);

  // Standalone NATS subscription: subscribe to toolkit:* actions directly over NATS.
  // In iframe mode, messages arrive via postMessage from the teach iframe bridge instead.
  const standaloneEnabled = isStandaloneSubscriptionEnabled(
    isLoading,
    session?.type,
    session?.userConfig?.mqttChannels?.self,
  );
  const entityId = session?.teacher?._id;

  const handleNatsMessage = useCallback((event: ToolkitNatsPayload) => {
    const clientId = clientIdRef.current;
    if (!clientId) return;

    // All toolkit payloads extend { senderId: string } and are structurally
    // compatible with Record<string, unknown>, so this spread is safe.
    const payload: Record<string, unknown> = { ...event.payload };

    peerDetectionRef.current?.handleNatsEvent({ action: event.action, payload });

    const reduxAction = mapNatsToRedux({ action: event.action, payload }, clientId);
    if (reduxAction) {
      dispatchToolkitReduxAction(reduxAction);
    }
  }, []);

  useRealtime({ entityId, enabled: !!standaloneEnabled }, [...NATS_TOOLKIT_ACTIONS], handleNatsMessage);
}
